import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumAddressLine } from "./enums/enumAddressLine.generated";
import { EnumBmEntityType } from "./enums/enumBmEntityType.generated";

// Model Objects

export class RegionalTax extends ModelObject<RegionalTax> {

    public id: Guid = Guid.createEmpty();
    public version: number = 0;
    public isHidden: boolean = false;
    public name: string = "";
    /**
    * Short Code, for posting to accounts, equivalent to SKU, must not overlap Items.
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public code: string = "";
    /**
    * The date (inclusive) on which the tax rates change. Determines whether to use previous rate or current rate.
    */
    public effectiveDate: LocalDate = LocalDate.ofEpochDay(0);
    /**
    * The tax rate that was in effect prior to the EffectiveDate.
    */
    public previousRate: number = 0;
    /**
    * The tax rate that is in effect on or after the EffectiveDate.
    */
    public currentRate: number = 0;
    /**
    * A semicolon-separated string of keywords used to identify relevant terms within address lines for determining regional tax applicability.
    * Not Nullable
    * Max Length : 1000
    */
    public addressLineMappingKeywords: string = "";
    /**
    * Address lines relevant for determining regional tax applicability.
    */
    public addressLinesMapping: (EnumAddressLine)[] = [];
    /**
    * Zero Rated VAT Code used for posting to accounts.
    */
    public vatCodeId: Guid = Guid.createEmpty();
    /**
    * Gl Code used for posting to accounts.
    */
    public glCodeId: Guid = Guid.createEmpty();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.version = jsonSerializedObject.version;
          this.isHidden = jsonSerializedObject.isHidden;
          this.name = jsonSerializedObject.name;
          this.code = jsonSerializedObject.code;
          this.effectiveDate = LocalDate.parse(jsonSerializedObject.effectiveDate);
          this.previousRate = jsonSerializedObject.previousRate;
          this.currentRate = jsonSerializedObject.currentRate;
          this.addressLineMappingKeywords = jsonSerializedObject.addressLineMappingKeywords;
          this.addressLinesMapping = jsonSerializedObject.addressLinesMapping.map((v: any) => EnumAddressLine.getByValue(v));
          this.vatCodeId = Guid.parseOrEmpty(jsonSerializedObject.vatCodeId);
          this.glCodeId = Guid.parseOrEmpty(jsonSerializedObject.glCodeId);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.version = this.version;
        toRet.isHidden = this.isHidden;
        toRet.name = this.name;
        toRet.code = this.code;
        toRet.effectiveDate = this.effectiveDate.toString();
        toRet.previousRate = this.previousRate;
        toRet.currentRate = this.currentRate;
        toRet.addressLineMappingKeywords = this.addressLineMappingKeywords;
        toRet.addressLinesMapping = this.addressLinesMapping.map(v => v.value);
        toRet.vatCodeId = this.vatCodeId.toString();
        toRet.glCodeId = this.glCodeId.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "RegionalTax.Id cannot be empty"},
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
            {field: "code", rule: (v: any) => v == null || v.length !== 0 || "Code cannot be empty"},
            {field: "code", rule: (v: any) => v == null || v.length <= 1000 || "Code has too many characters (1000)"},
            {field: "addressLineMappingKeywords", rule: (v: any) => v == null || v.length <= 1000 || "AddressLineMappingKeywords has too many characters (1000)"},
            {field: "vatCodeId", rule: (v: any) => !v.isEmpty() || "RegionalTax.VatCodeId cannot be empty"},
            {field: "glCodeId", rule: (v: any) => !v.isEmpty() || "RegionalTax.GlCodeId cannot be empty"},
    ];
}


export class RegionalTaxLine extends ModelObject<RegionalTaxLine> {

    public id: Guid = Guid.createEmpty();
    /**
    * Either be OrderId or CreditId
    */
    public parentId: Guid = Guid.createEmpty();
    public parentType: EnumBmEntityType = EnumBmEntityType.none;
    public regionalTaxId: Guid = Guid.createEmpty();
    public rate: number = 0;
    public litres: number = 0;
    public taxAmount: number = 0;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.parentId = Guid.parseOrEmpty(jsonSerializedObject.parentId);
          this.parentType = EnumBmEntityType.getByValue(jsonSerializedObject.parentType, this.parentType);
          this.regionalTaxId = Guid.parseOrEmpty(jsonSerializedObject.regionalTaxId);
          this.rate = jsonSerializedObject.rate;
          this.litres = jsonSerializedObject.litres;
          this.taxAmount = jsonSerializedObject.taxAmount;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.id = this.id.toString();
        toRet.parentId = this.parentId.toString();
        toRet.parentType = this.parentType.value;
        toRet.regionalTaxId = this.regionalTaxId.toString();
        toRet.rate = this.rate;
        toRet.litres = this.litres;
        toRet.taxAmount = this.taxAmount;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "id", rule: (v: any) => !v.isEmpty() || "RegionalTaxLine.Id cannot be empty"},
            {field: "parentId", rule: (v: any) => !v.isEmpty() || "RegionalTaxLine.ParentId cannot be empty"},
            {field: "regionalTaxId", rule: (v: any) => !v.isEmpty() || "RegionalTaxLine.RegionalTaxId cannot be empty"},
    ];
}


export class RegionalTaxService extends ServiceBase {

  public async getAllRegionalTaxes(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(RegionalTax)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.regionalTaxes.map((v: any) => new RegionalTax(v));;
    return this.makeJsonRequest("regionalTax/v1/getAllRegionalTaxes", req, false, extractor, opts);
  };

  public async saveRegionalTax(regionalTax: RegionalTax, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    regionalTax:regionalTax.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("regionalTax/v1/saveRegionalTax", req, true, extractor, opts);
  };

  public async setRegionalTaxIsHidden(regionalTaxId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    regionalTaxId:regionalTaxId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("regionalTax/v1/setRegionalTaxIsHidden", req, true, extractor, opts);
  };

  public async getRegionalTaxesForOutlet(outletId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(RegionalTax)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    outletId:outletId.toString(),
    };
    const extractor = (response:any) => response.regionalTaxes.map((v: any) => new RegionalTax(v));;
    return this.makeJsonRequest("regionalTax/v1/getRegionalTaxesForOutlet", req, false, extractor, opts);
  };

  public async getRegionalTaxes(regionalTaxIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(RegionalTax)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    regionalTaxIds:regionalTaxIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.regionalTaxes.map((v: any) => new RegionalTax(v));;
    return this.makeJsonRequest("regionalTax/v1/getRegionalTaxes", req, false, extractor, opts);
  };
}

export const regionalTaxService = new RegionalTaxService();